import { useState } from 'react';
import { useBillingCheckout } from '../../../platform/api/hooks';
import { Icon } from '../../../components/ui/Icon';
import { type CheckoutResult, newIdempotencyKey } from './billing.support';

export function CheckoutForm({ planCode }: { planCode: string }) {
  const checkout = useBillingCheckout();
  // Uma chave por intenção de compra: reenviar o mesmo formulário não duplica cobrança.
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [form, setForm] = useState({
    billingName: '',
    billingCpfCnpj: '',
    billingEmail: '',
    billingMobilePhone: '',
  });
  const result = checkout.data as CheckoutResult | undefined;

  if (result) {
    const { payment } = result;
    return (
      <div className="work-card" style={{ marginTop: '1.25rem', display: 'grid', gap: '0.8rem' }}>
        <strong>Pagamento criado.</strong>
        {payment.providerPageUrl && (
          <a className="btn btn-primary" href={payment.providerPageUrl} rel="noopener noreferrer">
            Ir para o pagamento <Icon name="arrowUp" size={15} />
          </a>
        )}
        {payment.pixQrCodeImage && (
          <img
            alt="QR Code PIX"
            src={
              payment.pixQrCodeImage.startsWith('data:')
                ? payment.pixQrCodeImage
                : `data:image/png;base64,${payment.pixQrCodeImage}`
            }
            style={{ width: 200, height: 200 }}
          />
        )}
        {payment.pixCopyPaste && (
          <label className="field">
            <span>PIX copia e cola</span>
            <input
              readOnly
              value={payment.pixCopyPaste}
              onFocus={(e) => {
                e.target.select();
              }}
            />
          </label>
        )}
        <p style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
          A assinatura é ativada quando o pagamento for confirmado pelo provedor.
        </p>
      </div>
    );
  }

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((current) => ({ ...current, [key]: e.target.value }));
  };

  return (
    <form
      className="work-card"
      style={{ marginTop: '1.25rem' }}
      onSubmit={(e) => {
        e.preventDefault();
        checkout.mutate({
          idempotencyKey,
          body: {
            planCode,
            billingName: form.billingName,
            billingCpfCnpj: form.billingCpfCnpj,
            ...(form.billingEmail ? { billingEmail: form.billingEmail } : {}),
            ...(form.billingMobilePhone ? { billingMobilePhone: form.billingMobilePhone } : {}),
          },
        });
      }}
    >
      <div className="field">
        <label htmlFor="billing-name">Nome para cobrança</label>
        <input
          id="billing-name"
          required
          maxLength={160}
          value={form.billingName}
          onChange={set('billingName')}
        />
      </div>
      <div className="field">
        <label htmlFor="billing-doc">CPF ou CNPJ</label>
        <input
          id="billing-doc"
          required
          inputMode="numeric"
          value={form.billingCpfCnpj}
          onChange={set('billingCpfCnpj')}
        />
      </div>
      <div className="field">
        <label htmlFor="billing-email">E-mail para cobrança (opcional)</label>
        <input
          id="billing-email"
          type="email"
          value={form.billingEmail}
          onChange={set('billingEmail')}
        />
      </div>
      <div className="field">
        <label htmlFor="billing-phone">Celular (opcional)</label>
        <input
          id="billing-phone"
          value={form.billingMobilePhone}
          onChange={set('billingMobilePhone')}
        />
      </div>
      {checkout.error && <p className="form-error">{checkout.error.message}</p>}
      <button type="submit" className="btn btn-primary" disabled={checkout.isPending}>
        {checkout.isPending ? 'Preparando pagamento…' : 'Continuar para o pagamento'}
        <Icon name="arrow" size={15} />
      </button>
    </form>
  );
}
